// src/lib/api.ts
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  ResponseType,
} from 'axios';
import { API_ENDPOINT, API_URL_USER_SERVICE } from './constant';
import {
  getCookies,
  getToken,
  logoutUser,
  Method,
  setCookies,
  showToastNotification,
} from '../utility/helper';
// src/lib/handleFormApiError.ts
import { FieldValues, UseFormSetError, Path } from 'react-hook-form';

export interface IApiErrorResponse {
  message: string;
  error?: string;
  statusCode: number;
}

interface RefreshTokenResponse {
  accessToken: string;
}

type AuthExcludedEndpoint =
  | 'login'
  | 'forgot'
  | 'sign-up'
  | 'reset-password'
  | 'refresh-token';

const authExcludedUrls: AuthExcludedEndpoint[] = [
  'login',
  'forgot',
  'sign-up',
  'reset-password',
  'refresh-token',
];

type CallAPIOptions<D = any> = {
  method: Method;
  headers?: Record<string, string>;
  data?: D;
  multipart?: boolean;
  responseType?: ResponseType;
  signal?: AbortSignal;
  onUploadProgress?: (progress: number) => void;
  onDownloadProgress?: (progress: number) => void;
};

/* Utility to check if endpoint should skip auth */
const isAuthExcluded = (endpoint: string): boolean => {
  return authExcludedUrls.some((pattern) => endpoint.includes(pattern));
};

/* Refresh token implementation */
const refreshToken = async (): Promise<string> => {
  try {
    const deviceToken = getCookies(
      `${import.meta.env.VITE_BUSINESS}deviceToken`,
    );
    const refreshToken = getCookies(
      `${import.meta.env.VITE_BUSINESS}refreshToken`,
    );

    const response = await axios.post<RefreshTokenResponse>(
      `${API_URL_USER_SERVICE + API_ENDPOINT.auth}refresh-token`,
      {
        refreshToken,
        deviceToken: deviceToken || 'default-device-token',
      },
    );

    const newToken = response.data.accessToken;
    setCookies(`${import.meta.env.VITE_BUSINESS}token`, newToken);
    return newToken;
  } catch (error) {
    logoutUser();
    console.error('error in refresh token', error);
    throw new Error('Failed to refresh session - please login again');
  }
};

/* Create Axios instance */
const axiosInstance = axios.create({
  baseURL: API_URL_USER_SERVICE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

/* Request Interceptor — attach auth token */
axiosInstance.interceptors.request.use((config) => {
  if (!isAuthExcluded(config.url || '')) {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* Response Interceptor — handle token refresh on 401 */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<IApiErrorResponse>) => {
    const originalRequest = error.config!;
    const status = error.response?.status;

    // if unauthorized & not excluded → try refresh
    if (status === 401 && !isAuthExcluded(originalRequest.url || '')) {
      try {
        const newToken = await refreshToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.log('refresh error', refreshError);
        logoutUser();
        return Promise.reject({
          message: 'Session expired - please login again',
          statusCode: 401,
        });
      }
    }

    // If we get here, it's not a 401 or refresh failed
    const apiError: IApiErrorResponse = {
      message:
        error.response?.data?.message ||
        error.message ||
        'Unknown error occurred',
      error: error.response?.data?.error,
      statusCode: status || 500,
    };

    return Promise.reject(apiError);
  },
);

const keywords = ['export', 'download-report'];

/* Generic callApi function */
export const callApi = async <T = any, D = any>(
  endpoint: string,
  options: CallAPIOptions<D>,
): Promise<T> => {
  const config: AxiosRequestConfig<D> = {
    url: endpoint,
    method: options.method,
    headers: options.headers,
    signal: options.signal,
    onUploadProgress: (event) => {
      if (options.onUploadProgress && event.total) {
        const progress = Math.round((event.loaded * 100) / event.total);
        options.onUploadProgress(progress);
      }
    },
    onDownloadProgress: (event) => {
      if (options.onDownloadProgress && event.total) {
        const progress = Math.round((event.loaded * 100) / event.total);
        options.onDownloadProgress(progress);
      }
    },
    ...(options.responseType ? { responseType: options.responseType } : {}),
  };

  if (
    options.method === 'POST' &&
    keywords.some((keyword) => endpoint.includes(keyword))
  ) {
    config.responseType = 'arraybuffer';
  }

  if (options.data !== null && options.data !== undefined) {
    config.data = options.data;
  }

  if (options.multipart && options.data instanceof FormData) {
    config.headers = {
      ...config.headers,
      'Content-Type': 'multipart/form-data',
    };
  }

  const response: AxiosResponse<T> = await axiosInstance(config);
  return response.data;
};
export interface ApiError {
  statusCode: number;
  message?: string;
}

/**
 * Reusable error handler for forms
 * @param err - The API error (with statusCode + message)
 * @param setError - react-hook-form setError function
 * @param map - Mapping of statusCodes to form fields + custom messages
 */
export function handleFormApiError<T extends FieldValues>(
  err: unknown,
  setError: UseFormSetError<T>,
  map: Record<
    number,
    {
      field: Path<T>; // which field gets the error
      message?: string; // custom error message
    }
  >,
) {
  const error = err as Partial<ApiError>;
  const config = map[error?.statusCode ?? -1];

  if (config) {
    setError(config.field, {
      type: 'manual',
      message: config.message || error.message || 'Something went wrong',
    });
  } else {
    // fallback → general error
    setError(Object.keys(map)[0] as Path<T>, {
      type: 'manual',
      message: error?.message || 'Unexpected error occurred',
    });
  }
}

export const ThrowError = (error: unknown, helperText = 'Error in API') => {
  const err = error as { message: string };
  showToastNotification('danger', err.message);
  console.log(helperText, error);
};
